import { useQuery } from '@tanstack/react-query';
import { workspaceService } from '@/services/workspace';
import { getUserIdFromJWT } from '@/services/auth';
import { accountService } from '@/services/accounts/accountService';
import { queryKeys } from '@/lib/queryKeys';
import { STALE } from '@/lib/queryPolicy';

export function useWorkspaces() {
  const userId = getUserIdFromJWT();

  return useQuery({
    queryKey: queryKeys.workspace.list(userId!),
    queryFn: async () => {
      if (!userId) return [];
      const workspaces = await workspaceService.listUserWorkspaces(userId);
      return workspaces || [];
    },
    enabled: !!userId,
    staleTime: STALE.stable, // workspaces don't change often
  });
}

/** Workspace membership list (`/workspaces/{id}/users`). Shared cache entry —
 *  mount from as many surfaces as needed, only one network call fires. */
export function useWorkspaceUsers(workspaceId: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.workspace.users(workspaceId ?? ''),
    queryFn: () => workspaceService.getWorkspaceUsers(workspaceId!),
    enabled: !!workspaceId,
    staleTime: STALE.stable,
  });
}

/** Owning-account members + roles (`/accounts/{id}/users`) for team management.
 *  Cross-account workspace members (and non-members) can't read the owning
 *  account; that's an expected 403 on every non-owner page load, so retries
 *  are disabled to avoid a retry storm. */
export function useAccountUsers(accountId: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.accounts.users(accountId ?? ''),
    queryFn: () => accountService.getAccountUsers(accountId!),
    enabled: !!accountId,
    staleTime: STALE.stable,
    retry: false,
  });
}
